import { CSSProperties } from 'react';
import { APP_EVENT_BUTTON_IDS as SDK_EVENT_BUTTON_IDS } from '@perxis-auto/instock-sdk/lib/shared/config/events';
import {
  CONSTRUCTOR_FIELDS_GROUP_MAP,
  CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP,
} from '@perxis-auto/instock-sdk/lib/shared/config/fieldsGroup';
import { mergeConstructor } from '@perxis-auto/instock-sdk/lib/shared/lib/constructor';
import type { ConfigStateType } from '@perxis-auto/instock-sdk/lib/shared/store/config';
import { ConstructorLayoutType, ConstructorWidgetEnum } from '@perxis-auto/instock-sdk/lib/shared/types/constructor';
import { I18nService } from '@perxis-auto/shared/lib/services/I18nService';

import { PAGES } from './pages';

/* ----- Filters ----- */
export const DEFAULT_FILTERS_IDS_MAP = {
  FILTERS: 'filters',
  FILTERS_LOCATION_GROUP: 'filters-location-group',
  FILTERS_CITY: 'filters-city',
  FILTERS_DEALERSHIP: 'filters-dealership',
  FILTERS_MODEL_GROUP: 'filters-model-group',
  FILTERS_MODEL: 'filters-model',
  FILTERS_PRICE_GROUP: 'filters-price-group',
  FILTERS_PRICE: 'filters-price',
  FILTERS_VERSION_GROUP: 'filters-version-group',
  FILTERS_VERSION: 'filters-version',
  FILTERS_ENGINE_GROUP: 'filters-engine-group',
  FILTERS_ENGINE: 'filters-engine',
  FILTERS_GEARBOX_GROUP: 'filters-gearbox-group',
  FILTERS_GEARBOX: 'filters-gearbox',
  FILTERS_DRIVETRAIN_GROUP: 'filters-drivetrain-group',
  FILTERS_DRIVETRAIN: 'filters-drivetrain',
  FILTERS_EXTERIOR_GROUP: 'filters-exterior-group',
  FILTERS_EXTERIOR: 'filters-exterior',
};

export const DEFAULT_FILTERS_KEYS_MAP = {
  CITY: 'city',
  DEALERSHIP: 'dealership',
  MODEL: 'model',
  PRICE: 'price',
  VERSION: 'version',
  ENGINE: 'engine',
  GEARBOX: 'gearbox',
  DRIVETRAIN: 'drivetrain',
  EXTERIOR: 'exterior',
};

export const GET_DEFAULT_FILTERS: (
  config: ConfigStateType,
) => ConstructorLayoutType<ConstructorWidgetEnum> = config => {
  const { i18n } = I18nService.getInstance();

  const isBenefitsEnabled = !!config.benefits?.enable;

  return {
    id: DEFAULT_FILTERS_IDS_MAP.FILTERS,
    type: ConstructorWidgetEnum.FilterPanel,
    itemStyle: {
      height: '100%',
      backgroundColor: '#ffffff',
    },
    items: [
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_LOCATION_GROUP,
        title: i18n.t('config.filters.location.title'),
        gutter: [10, 10],
        order: 5,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_CITY,
            type: ConstructorWidgetEnum.FilterLocation,
            key: DEFAULT_FILTERS_KEYS_MAP.CITY,
            path: 'locations.dealers_cities',
            span: 24,
            order: 1,
            hideSingleOption: true,
            props: {
              hideDisabledOptions: true,
            },
          },
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_DEALERSHIP,
            type: ConstructorWidgetEnum.FilterMultipleSelect,
            key: DEFAULT_FILTERS_KEYS_MAP.DEALERSHIP,
            path: 'locations.dealers_dealerships',
            span: 24,
            order: 2,
            hideSingleOption: true,
            resetOnChange: [DEFAULT_FILTERS_KEYS_MAP.CITY],
            props: {
              label: i18n.t('config.filters.location.dealership.label'),
              showSearch: true,
              hideDisabledOptions: true,
              maxTagCount: 1000,
              maxTagTextLength: 12,
              filterOption: (input: string, option: any) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
              filterSort: (a: any, b: any) =>
                // eslint-disable-next-line no-unsafe-optional-chaining
                a?.disabled - b?.disabled ||
                (a?.label ?? '').toLowerCase().localeCompare((b?.label ?? '').toLowerCase()),
            },
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_MODEL_GROUP,
        title: i18n.t('config.filters.models.title'),
        gutter: [20, 20],
        order: 10,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_MODEL,
            type: ConstructorWidgetEnum.FilterCheckboxGroup,
            key: DEFAULT_FILTERS_KEYS_MAP.MODEL,
            path: 'model',
            span: 24,
            order: 1,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_PRICE_GROUP,
        title: i18n.t('format.withCurrency', {
          value: i18n.t('config.filters.prices.title'),
        }),
        gutter: [20, 20],
        order: 15,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_PRICE,
            type: ConstructorWidgetEnum.FilterRangeSlider,
            key: DEFAULT_FILTERS_KEYS_MAP.PRICE,
            path: isBenefitsEnabled ? 'minPrice' : 'price',
            span: 24,
            order: 1,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_VERSION_GROUP,
        title: i18n.t('config.filters.versions.title'),
        gutter: [20, 20],
        order: 20,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_VERSION,
            type: ConstructorWidgetEnum.FilterCheckboxGroup,
            key: DEFAULT_FILTERS_KEYS_MAP.VERSION,
            path: 'version',
            dependencies: [DEFAULT_FILTERS_KEYS_MAP.MODEL],
            dependencyTextFormatter: () => i18n.t('config.filters.versions.dependencyText'),
            groupByKey: DEFAULT_FILTERS_KEYS_MAP.MODEL,
            span: 24,
            order: 1,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_ENGINE_GROUP,
        title: i18n.t('config.filters.engines.title'),
        gutter: [20, 20],
        order: 25,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_ENGINE,
            type: ConstructorWidgetEnum.FilterCheckboxGroup,
            key: DEFAULT_FILTERS_KEYS_MAP.ENGINE,
            path: 'engine',
            span: 24,
            order: 1,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_GEARBOX_GROUP,
        title: i18n.t('config.filters.gearboxes.title'),
        gutter: [20, 20],
        order: 30,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_GEARBOX,
            type: ConstructorWidgetEnum.FilterCheckboxGroup,
            key: DEFAULT_FILTERS_KEYS_MAP.GEARBOX,
            path: 'gearbox',
            span: 24,
            order: 1,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_DRIVETRAIN_GROUP,
        title: i18n.t('config.filters.drivetrains.title'),
        gutter: [20, 20],
        order: 35,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_DRIVETRAIN,
            type: ConstructorWidgetEnum.FilterCheckboxGroup,
            key: DEFAULT_FILTERS_KEYS_MAP.DRIVETRAIN,
            path: 'drivetrain',
            span: 24,
            order: 1,
            props: {},
          },
        ],
      },
      {
        id: DEFAULT_FILTERS_IDS_MAP.FILTERS_EXTERIOR_GROUP,
        title: i18n.t('config.filters.exteriors.title'),
        gutter: [20, 20],
        order: 40,
        items: [
          {
            id: DEFAULT_FILTERS_IDS_MAP.FILTERS_EXTERIOR,
            type: ConstructorWidgetEnum.FilterColorPicker,
            key: DEFAULT_FILTERS_KEYS_MAP.EXTERIOR,
            path: 'exterior',
            span: 24,
            order: 1,
            props: {},
          },
        ],
      },
    ],
  };
};
/* ----- Filters End ----- */

/* ----- CarTile ----- */
export const DEFAULT_CAR_TILE_IDS_MAP = {
  CAR_TILE: 'car-tile-root',
  CAR_TILE_MARKERS: 'car-tile-markers',
  CAR_TILE_MAIN: 'car-tile-main',
  CAR_TILE_INFO: 'car-tile-info',
  CAR_TILE_LOCATION: 'car-tile-location',
  CAR_TILE_SPECIFICATIONS: 'car-tile-specifications',
  CAR_TILE_HEADER: 'car-tile-header',
  CAR_TILE_STATUS: 'car-tile-status',
  CAR_TILE_FINANCE: 'car-tile-finance',
  CAR_TILE_LOCATION_AND_ACTIONS: 'car-tile-location-and-actions',
  CAR_TILE_FOOTER: 'car-tile-footer',
  CAR_TILE_PRICE: 'car-tile-price',
  CAR_TILE_PAYMENT: 'car-tile-payment',
  CAR_TILE_ACTIONS: 'car-tile-actions',
  CAR_TILE_GALLERY: 'car-tile-gallery',
};

export const GET_DEFAULT_CAR_TILE: (config: ConfigStateType) => ConstructorLayoutType<ConstructorWidgetEnum> = () => {
  return {
    id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE,
    type: ConstructorWidgetEnum.Tile,
    span: 24,
    gutter: [0, 'vertical-sky'],
    itemStyle: {
      height: '100%',
    },
    style: {
      height: '100%',
      alignContent: 'space-between',
    },
    props: {
      style: {
        height: '100%',
        minHeight: '400px',
      },
    },
    items: [
      {
        id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_MARKERS,
        type: ConstructorWidgetEnum.TileMarkers,
        span: 24,
        order: 1,
        colStyle: {
          position: 'absolute',
          top: 10,
          left: 0,
          zIndex: 2,
          paddingLeft: 10,
          paddingRight: 10,
          width: '100%',
        },
        props: {},
        fields: {
          [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['logisticStatus', 'tags'],
        },
      },
      {
        id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_MAIN,
        gutter: [0, 'vertical-blue'],
        span: 24,
        order: 3,
        items: [
          {
            id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_GALLERY,
            type: ConstructorWidgetEnum.TileGallery,
            span: 24,
            order: 1,
            props: {},
            fields: {
              [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['media', 'model'],
            },
          },
          {
            id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_INFO,
            gutter: [0, 'vertical-sky'],
            span: 24,
            order: 3,
            items: [
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_HEADER,
                type: ConstructorWidgetEnum.TileHeader,
                span: 24,
                order: 1,
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['model', 'version', 'productionDate'],
                },
              },
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_SPECIFICATIONS,
                type: ConstructorWidgetEnum.TileTable,
                span: 24,
                order: 3,
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['engine', 'gearbox', 'drivetrain'],
                },
              },
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_STATUS,
                type: ConstructorWidgetEnum.TileStatus,
                span: 24,
                order: 5,
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['logisticStatus'],
                },
              },
            ],
          },
        ],
      },
      {
        id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_FOOTER,
        gutter: [0, 'vertical-sky'],
        span: 24,
        order: 5,
        style: {
          flexDirection: 'column',
        },
        wrap: false,
        items: [
          {
            id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_FINANCE,
            gutter: [18, 2],
            span: 24,
            order: 1,
            items: [
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_PRICE,
                type: ConstructorWidgetEnum.TilePrice,
                flex: 0,
                order: 1,
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['price', 'minPrice', 'benefits'],
                },
              },
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_PAYMENT,
                type: ConstructorWidgetEnum.TilePayment,
                flex: 0,
                order: 3,
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['model', 'price'],
                },
              },
            ],
          },
          {
            id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_LOCATION_AND_ACTIONS,
            gutter: [8, 0],
            span: 24,
            order: 3,
            align: 'middle',
            justify: 'space-between',
            wrap: false,
            items: [
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_LOCATION,
                type: ConstructorWidgetEnum.TileLocation,
                flex: 'auto',
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['locations'],
                },
              },
              {
                id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_ACTIONS,
                type: ConstructorWidgetEnum.TileActions,
                flex: 'none',
                colStyle: {
                  marginLeft: 'auto',
                },
                props: {
                  redirects: {
                    onComparisonRedirect: {
                      to: PAGES.Comparison,
                    },
                    onFavoriteRedirect: {
                      to: PAGES.Favorites,
                    },
                  },
                },
              },
            ],
          },
        ],
      },
    ],
  };
};
/* ----- CarTile End ----- */

/* ----- Comparison CarTile ----- */
export const DEFAULT_COMPARISON_TILE_IDS_MAP = {
  COMPARISON_TILE: 'comparison-tile-root',
  COMPARISON_TILE_MARKERS: 'comparison-tile-markers',
  COMPARISON_TILE_MAIN: 'comparison-tile-main',
  COMPARISON_TILE_INFO: 'comparison-tile-info',
  COMPARISON_TILE_HEADER: 'comparison-tile-header',
  COMPARISON_TILE_STATUS: 'comparison-tile-status',
  COMPARISON_TILE_FOOTER: 'comparison-tile-footer',
  COMPARISON_TILE_FINANCE: 'comparison-tile-finance',
  COMPARISON_TILE_PRICE_DESKTOP: 'comparison-tile-price-desktop',
  COMPARISON_TILE_PRICE_MOBILE: 'comparison-tile-price-mobile',
  COMPARISON_TILE_PAYMENT: 'comparison-tile-payment',
  COMPARISON_TILE_LOCATION_AND_ACTIONS_DESKTOP: 'comparison-tile-location-and-actions-desktop',
  COMPARISON_TILE_LOCATION_AND_ACTIONS_MOBILE: 'comparison-tile-location-and-actions-mobile',
  COMPARISON_TILE_LOCATION: 'comparison-tile-location',
  COMPARISON_TILE_ACTIONS: 'comparison-tile-actions',
  COMPARISON_TILE_GALLERY: 'comparison-tile-gallery',
};

/**
 * Блоки по умолчанию, размещающиеся на странице сравнения
 */
export const GET_DEFAULT_COMPARISON_TILE: (
  config: ConfigStateType,
) => ConstructorLayoutType<ConstructorWidgetEnum> = () => {
  return {
    id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE,
    type: ConstructorWidgetEnum.Tile,
    span: 24,
    gutter: [0, 'vertical-sky'],
    itemStyle: {
      height: '100%',
    },
    style: {
      height: '100%',
      alignContent: 'space-between',
    },
    props: {
      hoverable: false,
      style: {
        height: '100%',
        minHeight: '280px',
        borderRadius: 0,
        '--perxis-auto-car-tile-image-border-radius': 0,
      },
    },
    items: [
      {
        id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_MARKERS,
        type: ConstructorWidgetEnum.TileMarkers,
        span: 24,
        order: 1,
        colStyle: {
          position: 'absolute',
          top: 10,
          left: 0,
          zIndex: 2,
          paddingLeft: 10,
          paddingRight: 10,
          width: '100%',
        } as CSSProperties,
        props: {},
        fields: {
          [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['logisticStatus', 'tags'],
        },
      },
      {
        id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_MAIN,
        gutter: [0, 'vertical-blue'],
        span: 24,
        order: 3,
        items: [
          {
            id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_GALLERY,
            type: ConstructorWidgetEnum.TileGallery,
            span: 24,
            order: 1,
            props: {},
            fields: {
              [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['media', 'model'],
            },
          },
          {
            id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_INFO,
            gutter: [0, 'vertical-blue'],
            span: 24,
            order: 3,
            items: [
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_HEADER,
                type: ConstructorWidgetEnum.TileHeader,
                span: 24,
                order: 1,
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['model', 'version', 'productionDate'],
                },
              },
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_STATUS,
                type: ConstructorWidgetEnum.TileStatus,
                span: 24,
                order: 5,
                style: {},
                colStyle: {
                  flex: 1,
                } as CSSProperties,
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['logisticStatus'],
                },
              },
            ],
          },
        ],
      },
      {
        id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_FOOTER,
        gutter: [0, 'vertical-blue'],
        span: 24,
        order: 5,
        items: [
          {
            id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_FINANCE,
            gutter: [18, 2],
            span: 24,
            order: 1,
            items: [
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_PRICE_DESKTOP,
                type: ConstructorWidgetEnum.TilePrice,
                xs: 0,
                sm: 24,
                order: 1,
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['price', 'minPrice', 'benefits'],
                },
              },
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_PRICE_MOBILE,
                type: ConstructorWidgetEnum.TilePrice,
                xs: 24,
                sm: 0,
                order: 1,
                props: {
                  oldValuePosition: 'bottom',
                },
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['price', 'minPrice', 'benefits'],
                },
              },
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_PAYMENT,
                type: ConstructorWidgetEnum.TilePayment,
                span: 24,
                order: 3,
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['model', 'price'],
                },
              },
            ],
          },
          {
            id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_LOCATION_AND_ACTIONS_DESKTOP,
            gutter: [8, 8],
            span: 24,
            order: 3,
            align: 'middle',
            wrap: false,
            xs: 0,
            sm: 24,
            items: [
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_LOCATION,
                type: ConstructorWidgetEnum.TileLocation,
                flex: 'auto',
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['locations'],
                },
              },
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_ACTIONS,
                type: ConstructorWidgetEnum.TileActions,
                flex: 'none',
                colStyle: {
                  marginLeft: 'auto',
                },
                props: {
                  isClickableComparison: false,
                  redirects: {
                    onComparisonRedirect: {
                      to: PAGES.Comparison,
                    },
                    onFavoriteRedirect: {
                      to: PAGES.Favorites,
                    },
                  },
                },
              },
            ],
          },
          {
            id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_LOCATION_AND_ACTIONS_MOBILE,
            gutter: [8, 8],
            span: 24,
            order: 5,
            align: 'middle',
            xs: 24,
            sm: 0,
            items: [
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_LOCATION,
                type: ConstructorWidgetEnum.TileLocation,
                span: 24,
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['locations'],
                },
              },
              {
                id: DEFAULT_COMPARISON_TILE_IDS_MAP.COMPARISON_TILE_ACTIONS,
                type: ConstructorWidgetEnum.TileActions,
                span: 24,
                props: {
                  isClickableComparison: false,
                  redirects: {
                    onComparisonRedirect: {
                      to: PAGES.Comparison,
                    },
                    onFavoriteRedirect: {
                      to: PAGES.Favorites,
                    },
                  },
                },
              },
            ],
          },
        ],
      },
    ],
  };
};

/* ----- Comparison CarTile End ----- */

/* ----- CarCard ----- */

export const DEFAULT_CAR_CARD_IDS_MAP = {
  CAR_CARD: 'car-card-root',
  CAR_CARD_MAIN: 'car-card-main',
  CAR_CARD_IMAGES_WITH_MARKERS: 'car-card-images-with-markers',
  CAR_CARD_MARKERS: 'car-card-markers',
  CAR_CARD_IMAGES: 'car-card-images',
  CAR_CARD_INFO: 'car-card-info',
  CAR_CARD_SPECIFICATIONS: 'car-card-specifications',
  CAR_CARD_LOCATION: 'car-card-location',
  CAR_CARD_FINANCE: 'car-card-finance',
  CAR_CARD_PRICE: 'car-card-price',
  CAR_CARD_PAYMENT: 'car-card-payment',
  CAR_CARD_BUTTONS: 'car-card-buttons',
  CAR_CARD_ACTIONS: 'car-card-actions',
  CAR_CARD_BOOKING: 'car-card-booking',
};

/**
 * Блоки по умолчанию, размещающиеся в блоке карточки автомобиля.
 */
export const GET_DEFAULT_CAR_CARD: (config: ConfigStateType) => ConstructorLayoutType<ConstructorWidgetEnum> = () => {
  return {
    id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD,
    type: ConstructorWidgetEnum.Card,
    span: 24,
    gutter: [0, 'vertical-orange'],
    items: [
      {
        id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_MAIN,
        span: 24,
        order: 1,
        gutter: [{ xs: 0, lg: 32, xl: 36, xxl: 42 }, 'vertical-orange'],
        items: [
          {
            id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_IMAGES_WITH_MARKERS,
            xs: 24,
            lg: 12,
            xl: 14,
            gutter: [0, 'vertical-green'],
            style: { position: 'relative' },
            items: [
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_MARKERS,
                type: ConstructorWidgetEnum.CardMarkers,
                order: 1,
                gutter: [0, 'vertical-green'],
                span: 24,
                colStyle: {
                  position: 'absolute',
                  zIndex: 1,
                  // Для организации корректных отступов относительно элемента
                  // который будет расположен ниже текущего.
                  borderStyle: 'solid',
                  borderColor: 'transparent',
                  borderWidth: '0px 12px',
                  paddingTop: '8px',
                  width: '100%',
                },
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['tags'],
                },
                referenceFields: {
                  [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: ['tags'],
                },
              },
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_IMAGES,
                type: ConstructorWidgetEnum.CardImage,
                order: 3,
                span: 24,
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['videos', 'media', 'model'],
                },
                referenceFields: {
                  [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: ['media', 'model'],
                },
              },
            ],
          },
          {
            id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_INFO,
            xs: 24,
            lg: 12,
            xl: 10,
            order: 9,
            gutter: [0, 0],
            items: [
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_SPECIFICATIONS,
                type: ConstructorWidgetEnum.CardTable,
                xs: { span: 24, order: 15 },
                sm: { span: 24, order: 11 },
                itemStyle: {
                  paddingBottom: 'var(--perxis-auto-gap-vertical-green-inner)',
                },
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: [
                    'engine',
                    'gearbox',
                    'drivetrain',
                    'version',
                    'exterior',
                    'interior',
                    'vin',
                  ],
                },
                referenceFields: {
                  [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: [
                    'engine',
                    'gearbox',
                    'drivetrain',
                    'version',
                    'exterior',
                    'interior',
                  ],
                },
              },
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_LOCATION,
                type: ConstructorWidgetEnum.CardLocation,
                xs: { span: 24, order: 17 },
                sm: { span: 24, order: 13 },
                itemStyle: {
                  paddingBottom: 'var(--perxis-auto-gap-vertical-yellow-inner)',
                },
                props: {},
                fields: {
                  [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['locations'],
                },
                referenceFields: {
                  [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: ['locations'],
                },
              },
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_FINANCE,
                xs: { span: 24, order: 11 },
                sm: { span: 24, order: 15 },
                gutter: [18, 'vertical-sky'],
                span: 24,
                style: {
                  paddingBottom: 'var(--perxis-auto-gap-vertical-green-inner)',
                },
                items: [
                  {
                    id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_PRICE,
                    type: ConstructorWidgetEnum.CardPrice,
                    xs: 24,
                    sm: 12,
                    props: {},
                    fields: {
                      [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['price', 'minPrice', 'benefits'],
                    },
                    referenceFields: {
                      [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: ['benefits'],
                    },
                  },
                  {
                    id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_PAYMENT,
                    type: ConstructorWidgetEnum.CardPayment,
                    xs: 24,
                    sm: 12,
                    props: {},
                    fields: {
                      [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['model', 'price'],
                    },
                    referenceFields: {
                      [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: ['model'],
                    },
                  },
                ],
              },
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_BUTTONS,
                xs: { span: 24, order: 13 },
                sm: { span: 24, order: 17 },
                gutter: [18, 'vertical-sky'],
                span: 24,
                style: {
                  paddingBottom: 'var(--perxis-auto-gap-vertical-green-inner)',
                },
                items: [
                  {
                    id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_BOOKING,
                    type: ConstructorWidgetEnum.CardBookingButton,
                    xs: 24,
                    sm: 12,
                    props: {
                      iconName: 'forward',
                      fullWidth: 'lg',
                      scrollTo: `#${DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_BOOKING_REQUEST}`,
                    },
                  },
                ],
              },
              {
                id: DEFAULT_CAR_CARD_IDS_MAP.CAR_CARD_ACTIONS,
                type: ConstructorWidgetEnum.CardActions,
                xs: { span: 24, order: 18 },
                sm: { span: 24, order: 18 },
                props: {
                  redirects: {
                    onComparisonRedirect: {
                      to: PAGES.Comparison,
                    },
                    onFavoriteRedirect: {
                      to: PAGES.Favorites,
                    },
                  },
                },
              },
            ],
          },
        ],
      },
    ],
  };
};
/* ----- CarCard End ----- */

/* ----- CarList ----- */
export const DEFAULT_CAR_LIST_IDS_MAP = {
  CAR_LIST: 'car-list-root',
  CAR_LIST_HEADER: 'car-list-header',
  CAR_LIST_FILTER_HISTORY: 'car-list-filters-history',
  CAR_LIST_CATALOG: 'car-list-catalog',
};

export const GET_DEFAULT_CAR_LIST: (
  config: ConfigStateType,
) => ConstructorLayoutType<ConstructorWidgetEnum> = config => {
  return {
    id: DEFAULT_CAR_LIST_IDS_MAP.CAR_LIST,
    type: ConstructorWidgetEnum.VehiclesList,
    order: 1,
    span: 24,
    flex: 'auto',
    style: {
      '--pa-car-list-root-background-color-inner': 'var(--pa-car-list-root-background-color, #f2f0f0)',
      backgroundColor: 'var(--pa-car-list-root-background-color-inner)',
      paddingTop: 'var(--perxis-auto-gap-vertical-sky-inner)',
      paddingBottom: 'var(--perxis-auto-gap-vertical-pink-inner)',
      paddingLeft: 20,
      paddingRight: 20,
    },
    items: [
      {
        id: DEFAULT_CAR_LIST_IDS_MAP.CAR_LIST_FILTER_HISTORY,
        type: ConstructorWidgetEnum.FilterHistory,
        order: 1,
        span: 24,
        itemStyle: {
          paddingBottom: 'var(--perxis-auto-gap-vertical-green-inner)',
        },
      },
      {
        id: DEFAULT_CAR_LIST_IDS_MAP.CAR_LIST_HEADER,
        type: ConstructorWidgetEnum.VehiclesCatalogHeader,
        order: 3,
        span: 24,
        itemStyle: {
          paddingBottom: 'var(--perxis-auto-gap-vertical-green-inner)',
        },
      },
      {
        id: DEFAULT_CAR_LIST_IDS_MAP.CAR_LIST_CATALOG,
        type: ConstructorWidgetEnum.VehiclesCatalog,
        order: 5,
        span: 24,
        style: {
          height: '100%',
        },
        props: {
          redirects: {
            link: {
              to: PAGES.Car,
            },
          },
        },
        items: [GET_DEFAULT_CAR_TILE(config)],
      },
    ],
  };
};
/* ----- CarList End ----- */

/* ----- Favorite CarList ----- */
export const GET_DEFAULT_FAVORITES_LIST: (
  config: ConfigStateType,
) => ConstructorLayoutType<ConstructorWidgetEnum> = config => {
  return {
    id: DEFAULT_CAR_LIST_IDS_MAP.CAR_LIST,
    type: ConstructorWidgetEnum.FavoritesList,
    span: 24,
    style: {
      height: '100%',
    },
    itemStyle: {
      paddingBottom: 'var(--perxis-auto-gap-vertical-pink-inner)',
    },
    props: {
      redirects: {
        onBackClick: {
          to: PAGES.Cars,
        },
        link: {
          to: PAGES.Car,
        },
      },
    },
    items: [
      mergeConstructor(GET_DEFAULT_CAR_TILE(config), {
        items: [
          {
            id: DEFAULT_CAR_TILE_IDS_MAP.CAR_TILE_ACTIONS,
            props: {
              isClickableFavorite: false,
              redirects: {
                onComparisonRedirect: {
                  to: PAGES.Comparison,
                },
                onFavoriteRedirect: {
                  to: PAGES.Favorites,
                },
              },
            },
          },
        ],
      }),
    ],
  };
};
/* ----- Favorite CarList End ----- */

/* ----- Favorite CarList End ----- */
export const DEFAULT_MODEL_LIST_IDS_MAP = {
  MODEL_LIST: 'model-list-root',
  MODEL_LIST_CATALOG: 'model-list-catalog',
};

export const GET_DEFAULT_MODEL_LIST: (
  config: ConfigStateType,
) => ConstructorLayoutType<ConstructorWidgetEnum> = config => {
  return {
    id: DEFAULT_MODEL_LIST_IDS_MAP.MODEL_LIST,
    span: 24,
    gutter: [0, 'vertical-yellow'],
    flex: 'auto',
    style: {
      '--pa-model-list-root-background-color-inner': 'var(--pa-model-list-root-background-color, #f2f0f0)',
      backgroundColor: 'var(--pa-model-list-root-background-color-inner)',

      paddingTop: 'var(--perxis-auto-gap-vertical-sky-inner)',
      paddingBottom: 'var(--perxis-auto-gap-vertical-pink-inner)',
      paddingLeft: 20,
      paddingRight: 20,
    },
    items: [
      {
        id: DEFAULT_MODEL_LIST_IDS_MAP.MODEL_LIST_CATALOG,
        type: ConstructorWidgetEnum.ModelList,
        order: 1,
        span: 24,
        props: {
          redirects: {
            onShowAllClick: {
              to: PAGES.Cars,
            },
            onModelClick: {
              to: PAGES.Cars,
            },
          },
        },
        items: [GET_DEFAULT_CAR_TILE(config)],
      },
    ],
  };
};

/* ----- Favorite CarList End ----- */

export const DEFAULT_CONSTRUCTOR_IDS_MAP = {
  CONSTRUCTOR_CARS_PAGE: 'cars-page',
  CONSTRUCTOR_CARS_HEADER: 'cars-header',
  CONSTRUCTOR_CARS_CATALOG: 'cars-catalog',
  CONSTRUCTOR_CAR_PAGE: 'car-page',
  CONSTRUCTOR_CAR_HEADER_AND_CARD: 'car-header-and-card',
  CONSTRUCTOR_CAR_HEADER: 'car-header',
  CONSTRUCTOR_CAR_CARD: 'car-card',
  CONSTRUCTOR_CAR_FINANCE: 'car-finance',
  CONSTRUCTOR_CAR_DEALER_COMMENT: 'car-dealer-comment',
  CONSTRUCTOR_CAR_DEALER_MAP: 'car-dealer-map',
  CONSTRUCTOR_CAR_BENEFITS: 'car-benefits',
  CONSTRUCTOR_CAR_EQUIPMENTS: 'car-equipments',
  CONSTRUCTOR_CAR_BOOKING_REQUEST: 'car-booking-request',
  CONSTRUCTOR_CAR_BENEFITS_FOOTNOTE: 'car-benefits-footnote',
  CONSTRUCTOR_MODELS_PAGE: 'models-page',
  CONSTRUCTOR_COMPARISON_PAGE: 'comparison-page',
  CONSTRUCTOR_COMPARISON_HEADER: 'comparison-header',
  CONSTRUCTOR_COMPARISON_FOOTER: 'comparison-footer',
  CONSTRUCTOR_COMPARISON_FOOTER_BUTTON_DESKTOP: 'comparison-footer-button-desktop',
  CONSTRUCTOR_COMPARISON_FOOTER_BUTTON_MOBILE: 'comparison-footer-button-mobile',
  CONSTRUCTOR_COMPARISON_LIST: 'comparison-list',
  CONSTRUCTOR_COMPARISON_EQUIPMENTS: 'comparison-equipments',
  CONSTRUCTOR_COMPARISON_SPECIFICATIONS: 'comparison-specifications',
  CONSTRUCTOR_FAVORITES_PAGE: 'favorites-page',
  CONSTRUCTOR_FAVORITES_HEADER: 'favorites-header',
  CONSTRUCTOR_FAVORITES_LIST: 'favorites-list',
  ...DEFAULT_FILTERS_IDS_MAP,
  ...DEFAULT_CAR_TILE_IDS_MAP,
  ...DEFAULT_COMPARISON_TILE_IDS_MAP,
  ...DEFAULT_CAR_CARD_IDS_MAP,
  ...DEFAULT_CAR_LIST_IDS_MAP,
  ...DEFAULT_MODEL_LIST_IDS_MAP,
};

export const GET_DEFAULT_APP_CONSTRUCTOR = (
  config: ConfigStateType,
): Record<string, ConstructorLayoutType<ConstructorWidgetEnum>> => {
  const { i18n } = I18nService.getInstance();

  return {
    [PAGES.Cars]: {
      id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CARS_PAGE,
      gutter: [0, 0],
      items: [
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CARS_HEADER,
          type: ConstructorWidgetEnum.VehiclesHeader,
          order: 1,
          span: 24,
          colStyle: {
            position: 'sticky',
            top: 0,
            left: 0,
            zIndex: 1000,
          },
          props: {
            redirects: {
              onFavoriteClick: {
                to: PAGES.Favorites,
              },
              onComparisonClick: {
                to: PAGES.Comparison,
              },
            },
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CARS_CATALOG,
          order: 3,
          span: 24,
          gutter: [0, 0],
          wrap: false,
          items: [GET_DEFAULT_FILTERS(config), GET_DEFAULT_CAR_LIST(config)],
        },
      ],
    },
    [PAGES.Models]: {
      id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_MODELS_PAGE,
      items: [
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CARS_HEADER,
          type: ConstructorWidgetEnum.VehiclesHeader,
          order: 1,
          span: 24,
          props: {
            isFilterPanelHidden: true,
            redirects: {
              onFavoriteClick: {
                to: PAGES.Favorites,
              },
              onComparisonClick: {
                to: PAGES.Comparison,
              },
            },
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CARS_CATALOG,
          order: 3,
          span: 24,
          gutter: [0, 0],
          wrap: false,
          items: [
            {
              id: DEFAULT_FILTERS_IDS_MAP.FILTERS,
              type: ConstructorWidgetEnum.FilterPanel,
              itemStyle: {
                height: '100%',
                backgroundColor: '#ffffff',
              },
              span: 0,
              items: [
                {
                  id: DEFAULT_FILTERS_IDS_MAP.FILTERS_MODEL_GROUP,
                  title: i18n.t('config.filters.models.title'),
                  gutter: [20, 20],
                  order: 10,
                  items: [
                    {
                      id: DEFAULT_FILTERS_IDS_MAP.FILTERS_MODEL,
                      type: ConstructorWidgetEnum.FilterModels,
                      key: DEFAULT_FILTERS_KEYS_MAP.MODEL,
                      path: 'model',
                      span: 24,
                      order: 1,
                      props: {},
                    },
                  ],
                },
              ],
            },
            GET_DEFAULT_MODEL_LIST(config),
          ],
        },
      ],
    },
    [PAGES.Car]: {
      id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_PAGE,
      items: [
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_HEADER_AND_CARD,
          order: 0,
          span: 24,
          withContainer: true,
          containerProps: {
            style: {
              paddingTop: 'var(--perxis-auto-gap-vertical-red-inner)',
              paddingBottom: 'var(--perxis-auto-gap-vertical-red-inner)',
              backgroundColor: '#FFFFFF',
            },
          },
          items: [
            {
              id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_HEADER,
              type: ConstructorWidgetEnum.VehicleHeader,
              order: 0,
              span: 24,
              props: {
                redirects: {
                  onBackClick: {
                    to: PAGES.Cars,
                  },
                },
              },
              itemStyle: {
                paddingBottom: 'var(--perxis-auto-gap-vertical-orange-inner)',
              },
              referenceFields: {
                [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: [
                  'logisticStatus',
                  'tags',
                  'model',
                  'version',
                ],
              },
            },
            {
              id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_CARD,
              order: 3,
              span: 24,
              items: [GET_DEFAULT_CAR_CARD(config)],
            },
          ],
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_FINANCE,
          type: ConstructorWidgetEnum.Finance,
          order: 3,
          span: 24,
          withContainer: true,
          containerProps: {
            style: {
              backgroundColor: '#F2F0F0',
            },
          },
          itemStyle: {
            paddingTop: 'var(--perxis-auto-gap-vertical-orange-inner)',
            paddingBottom: 'var(--perxis-auto-gap-vertical-orange-inner)',
          },
          referenceFields: {
            [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: ['model'],
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_DEALER_COMMENT,
          type: ConstructorWidgetEnum.DealerComment,
          order: 5,
          span: 24,
          withContainer: true,
          containerProps: {
            style: {
              backgroundColor: '#FFFFFF',
            },
          },
          itemStyle: {
            paddingTop: 'var(--perxis-auto-gap-vertical-orange-inner)',
            paddingBottom: 'var(--perxis-auto-gap-vertical-orange-inner)',
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_DEALER_MAP,
          type: ConstructorWidgetEnum.DealerMap,
          order: 7,
          span: 24,
          referenceFields: {
            [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: ['locations'],
            [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.DEALER_GET_DEALERSHIP]: ['departments'],
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_BENEFITS,
          type: ConstructorWidgetEnum.Benefits,
          order: 9,
          span: 24,
          withContainer: true,
          containerProps: {
            style: {
              backgroundColor: '#F2F0F0',
            },
          },
          itemStyle: {
            paddingTop: 'var(--perxis-auto-gap-vertical-orange-inner)',
            paddingBottom: 'var(--perxis-auto-gap-vertical-orange-inner)',
          },
          referenceFields: {
            [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: ['benefits', 'maxBenefit'],
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_EQUIPMENTS,
          type: ConstructorWidgetEnum.Equipments,
          order: 11,
          span: 24,
          withContainer: true,
          containerProps: {
            style: {
              backgroundColor: '#FFFFFF',
            },
          },
          itemStyle: {
            paddingTop: 'var(--perxis-auto-gap-vertical-orange-inner)',
            paddingBottom: 'var(--perxis-auto-gap-vertical-orange-inner)',
          },
          fields: {
            [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_EQUIPMENT_GET_SPECIFICATION_GROUPS]: [
              'alternateName',
              'id',
              'name',
              'position',
            ],
            [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_EQUIPMENT_GET_SPECIFICATIONS]: [
              'alternateName',
              'dataType',
              'group',
              'id',
              'name',
              'position',
            ],
          },
          referenceFields: {
            [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: ['options', 'specificationsValues'],
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_BOOKING_REQUEST,
          type: ConstructorWidgetEnum.BookingRequest,
          order: 15,
          span: 24,
          withContainer: true,
          containerProps: {
            style: {
              backgroundColor: '#F2F0F0',
            },
          },
          itemStyle: {
            paddingTop: 'var(--perxis-auto-gap-vertical-red-inner)',
            paddingBottom: 'var(--perxis-auto-gap-vertical-pink-inner)',
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_BENEFITS_FOOTNOTE,
          type: ConstructorWidgetEnum.BenefitsFootnote,
          order: 21,
          span: 24,
          withContainer: true,
          containerProps: {
            style: {
              backgroundColor: '#FFFFFF',
            },
          },
          itemStyle: {
            paddingTop: 'var(--perxis-auto-gap-vertical-orange-inner)',
            paddingBottom: 'var(--perxis-auto-gap-vertical-orange-inner)',
          },
          referenceFields: {
            [CONSTRUCTOR_REFERENCE_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLE]: ['benefits'],
          },
        },
      ],
    },
    [PAGES.Comparison]: {
      id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_COMPARISON_PAGE,
      style: {
        backgroundColor: '#FFFFFF',
      },
      items: [
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_COMPARISON_HEADER,
          type: ConstructorWidgetEnum.ComparisonHeader,
          order: 1,
          span: 24,
          withContainer: true,
          itemStyle: {
            paddingTop: 'var(--perxis-auto-gap-vertical-orange-inner)',
            paddingBottom: 'var(--perxis-auto-gap-vertical-orange-inner)',
          },
          props: {
            redirects: {
              onBackClick: {
                to: PAGES.Cars,
              },
            },
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_COMPARISON_LIST,
          type: ConstructorWidgetEnum.ComparisonList,
          order: 3,
          span: 24,
          className: 'instock-widget-comparison-list',
          colStyle: {
            top: 0,
            zIndex: 900,
            position: 'relative',
            '--pa-comparison-list-compact-tile-markers-display': 'none',
            '--pa-comparison-list-compact-tile-status-display': 'none',
            '--pa-comparison-list-compact-tile-location-display': 'none',
            '--pa-comparison-list-compact-tile-actions-display': 'none',
          } as CSSProperties,
          props: {
            containerSelector: '.instock-widget-comparison-list',
            redirects: {
              onBackClick: {
                to: PAGES.Cars,
              },
              link: {
                to: PAGES.Car,
              },
            },
          },
          items: [GET_DEFAULT_COMPARISON_TILE(config)],
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_COMPARISON_SPECIFICATIONS,
          type: ConstructorWidgetEnum.ComparisonSpecifications,
          order: 5,
          span: 24,
          withContainer: true,
          itemStyle: {
            paddingBottom: 'var(--perxis-auto-gap-vertical-yellow-inner)',
          },
          fields: {
            [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: [
              'engine',
              'gearbox',
              'drivetrain',
              'version',
              'productionDate',
            ],
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_COMPARISON_EQUIPMENTS,
          type: ConstructorWidgetEnum.ComparisonEquipments,
          order: 7,
          span: 24,
          withContainer: true,
          itemStyle: {
            paddingBottom: 'var(--perxis-auto-gap-vertical-yellow-inner)',
          },
          fields: {
            [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_GET_VEHICLES]: ['options', 'specificationsValues'],
            [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_EQUIPMENT_GET_OPTION_GROUPS]: ['alternateName', 'name', 'position'],
            [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_EQUIPMENT_GET_SPECIFICATION_GROUPS]: [
              'alternateName',
              'id',
              'name',
              'position',
            ],
            [CONSTRUCTOR_FIELDS_GROUP_MAP.VEHICLE_EQUIPMENT_GET_SPECIFICATIONS]: [
              'alternateName',
              'dataType',
              'group',
              'id',
              'name',
              'position',
            ],
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_COMPARISON_FOOTER,
          type: ConstructorWidgetEnum.ComparisonFooter,
          order: 9,
          span: 24,
          withContainer: true,
          itemStyle: {
            paddingBottom: 'var(--perxis-auto-gap-vertical-orange-inner)',
          },
          items: [
            {
              id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_COMPARISON_FOOTER_BUTTON_DESKTOP,
              type: ConstructorWidgetEnum.Button,
              order: 1,
              xs: 0,
              sm: 0,
              md: 24,
              props: {
                id: SDK_EVENT_BUTTON_IDS.BOOKING_FROM_COMPARISON_PAGE,
                children: i18n.t('widgets.comparisonFooter.bookingDesktop'),
                iconName: 'forward',
                type: 'primary',
                redirects: {
                  onClick: {
                    to: `${PAGES.Car.replace(':id', '{{carId}}')}?scroll-to=${encodeURIComponent(`#${DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_BOOKING_REQUEST}`)}`,
                  },
                },
              },
            },
            {
              id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_COMPARISON_FOOTER_BUTTON_MOBILE,
              type: ConstructorWidgetEnum.Button,
              order: 1,
              span: 24,
              sm: 24,
              md: 0,
              props: {
                id: SDK_EVENT_BUTTON_IDS.BOOKING_FROM_COMPARISON_PAGE,
                children: i18n.t('widgets.comparisonFooter.bookingMobile'),
                iconName: 'forward',
                type: 'primary',
                redirects: {
                  onClick: {
                    to: `${PAGES.Car.replace(':id', '{{carId}}')}?scroll-to=${encodeURIComponent(`#${DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_CAR_BOOKING_REQUEST}`)}`,
                  },
                },
              },
            },
          ],
        },
      ],
    },
    [PAGES.Favorites]: {
      id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_FAVORITES_PAGE,
      items: [
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_FAVORITES_HEADER,
          type: ConstructorWidgetEnum.FavoritesHeader,
          order: 1,
          span: 24,
          withContainer: true,
          itemStyle: {
            paddingTop: 'var(--perxis-auto-gap-vertical-orange-inner)',
            paddingBottom: 'var(--perxis-auto-gap-vertical-orange-inner)',
          },
          props: {
            redirects: {
              onBackClick: {
                to: PAGES.Cars,
              },
            },
          },
        },
        {
          id: DEFAULT_CONSTRUCTOR_IDS_MAP.CONSTRUCTOR_FAVORITES_LIST,
          order: 3,
          span: 24,
          withContainer: true,
          containerProps: {
            style: {
              backgroundColor: '#F2F0F0',
            },
          },
          items: [GET_DEFAULT_FAVORITES_LIST(config)],
        },
      ],
    },
  };
};
